import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../config/api';
import PartnerPlanWall from '../components/PartnerPlanWall';
import {
  PartnerLoading,
  PartnerPageHeader,
  PartnerPageShell,
  partnerOxbloodBtnSx,
} from '../components/PartnerListChrome';
import { DOCUMENT } from '../constants/documentTheme';
import usePartnerEntitlements from '../hooks/usePartnerEntitlements';

const PERIODS = ['7d', '30d', '90d', '1y'];

const PartnerAnalytics = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { entitlements, loading: entitlementsLoading } = usePartnerEntitlements();
  const hasAnalytics = Boolean(entitlements?.features?.hasAnalytics);
  const [period, setPeriod] = useState('30d');
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [planWallOpen, setPlanWallOpen] = useState(false);

  const load = useCallback(async () => {
    if (!hasAnalytics) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError('');
      const response = await api.get(`/partner-packages/analytics?period=${period}`, {
        usePartnerAuth: true,
      });
      setAnalytics(response.data.analytics || null);
    } catch (err) {
      if (err.response?.status === 403) {
        setError(t('partnerAnalytics.upgradeRequired'));
      } else {
        setError(err.response?.data?.error || t('partnerAnalytics.loadFailed'));
      }
    } finally {
      setLoading(false);
    }
  }, [hasAnalytics, period, t]);

  useEffect(() => {
    if (!entitlementsLoading) load();
  }, [entitlementsLoading, load]);

  if (entitlementsLoading || loading) {
    return (
      <PartnerPageShell>
        <PartnerLoading />
      </PartnerPageShell>
    );
  }

  if (!hasAnalytics) {
    return (
      <PartnerPageShell>
        <PartnerPageHeader
          title={t('partnerAnalytics.title')}
          subtitle={t('partnerAnalytics.subtitle')}
        />
        <Box
          sx={{
            mt: 4,
            p: 3,
            border: `1px solid ${DOCUMENT.ruleHard}`,
            bgcolor: DOCUMENT.surface,
          }}
        >
          <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 22, mb: 1 }}>
            {t('partnerAnalytics.lockedTitle')}
          </Typography>
          <Typography sx={{ fontSize: 14, color: DOCUMENT.ink2, mb: 2.5, maxWidth: '56ch' }}>
            {t('partnerAnalytics.lockedBody')}
          </Typography>
          <Button sx={partnerOxbloodBtnSx} onClick={() => setPlanWallOpen(true)}>
            {t('partnerAnalytics.upgrade')}
          </Button>
        </Box>
        <PartnerPlanWall
          open={planWallOpen}
          onClose={() => setPlanWallOpen(false)}
          onSuccess={() => {
            setPlanWallOpen(false);
            navigate('/partner/billing');
          }}
          reason="billing"
          entitlements={entitlements}
        />
      </PartnerPageShell>
    );
  }

  const packages = analytics?.packages || {};
  const revenue = analytics?.revenue || {};
  const topPackages = analytics?.top_packages || [];

  return (
    <PartnerPageShell error={error} onClearError={() => setError('')}>
      <PartnerPageHeader
        title={t('partnerAnalytics.title')}
        subtitle={t('partnerAnalytics.subtitle')}
        actions={(
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {PERIODS.map((value) => (
              <Button
                key={value}
                data-testid={`analytics-period-${value}`}
                onClick={() => setPeriod(value)}
                sx={{
                  ...partnerOxbloodBtnSx,
                  bgcolor: period === value ? DOCUMENT.oxblood : 'transparent',
                  color: period === value ? DOCUMENT.ivory : DOCUMENT.oxblood,
                  border: `1px solid ${DOCUMENT.oxblood}`,
                  '&:hover': {
                    bgcolor: period === value ? DOCUMENT.nav : '#F5F0E6',
                  },
                }}
              >
                {t(`partnerAnalytics.period.${value}`)}
              </Button>
            ))}
          </Box>
        )}
      />

      <Box
        data-testid="partner-analytics"
        sx={{
          mt: 3,
          display: 'grid',
          gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
          gap: '1px',
          bgcolor: DOCUMENT.ruleHard,
          border: `1px solid ${DOCUMENT.ruleHard}`,
        }}
      >
        {[
          { label: t('partnerAnalytics.views'), value: packages.total_views || 0 },
          { label: t('partnerAnalytics.inquiryStarted'), value: analytics?.funnel?.inquiry_started || 0 },
          { label: t('partnerAnalytics.inquiries'), value: packages.total_inquiries || 0 },
          { label: t('partnerAnalytics.bookings'), value: revenue.booking_count || packages.total_bookings || 0 },
          { label: t('partnerAnalytics.grossRevenue'), value: Number(revenue.gross_revenue || 0).toFixed(0) },
          { label: t('partnerAnalytics.proposals'), value: analytics?.funnel?.proposals || 0 },
          { label: t('partnerAnalytics.inquiryConversion'), value: `${Math.round((analytics?.funnel?.inquiry_conversion || 0) * 100)}%` },
          { label: t('partnerAnalytics.bookingFromInquiry'), value: `${Math.round((analytics?.funnel?.booking_from_inquiry || 0) * 100)}%` },
          { label: t('partnerAnalytics.platformFees'), value: Number(analytics?.funnel?.platform_fees || revenue.platform_fees || 0).toFixed(0) },
        ].map((tile) => (
          <Box key={tile.label} sx={{ bgcolor: DOCUMENT.surface, p: 2.25 }}>
            <Typography sx={{ fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase', color: DOCUMENT.ink3 }}>
              {tile.label}
            </Typography>
            <Typography sx={{ mt: 1, fontSize: 28, fontVariantNumeric: 'tabular-nums' }}>
              {tile.value}
            </Typography>
          </Box>
        ))}
      </Box>

      <Box sx={{ mt: 4 }}>
        <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 22, mb: 1.5 }}>
          {t('partnerAnalytics.topPackages')}
        </Typography>
        {topPackages.length === 0 ? (
          <Alert severity="info">{t('partnerAnalytics.noTopPackages')}</Alert>
        ) : (
          <Box sx={{ borderTop: `1px solid ${DOCUMENT.ruleHard}` }}>
            {topPackages.map((pkg) => (
              <Box
                key={pkg.id}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: '1.5fr 1fr 5rem 6rem' },
                  gap: 1,
                  py: 1.75,
                  borderBottom: `1px solid ${DOCUMENT.rule}`,
                }}
              >
                <Typography sx={{ fontSize: 15 }}>{pkg.title}</Typography>
                <Typography sx={{ fontSize: 13, color: DOCUMENT.ink3 }}>{pkg.destination}</Typography>
                <Typography sx={{ fontVariantNumeric: 'tabular-nums' }}>
                  {pkg.bookings} {t('partnerAnalytics.bookingsShort')}
                </Typography>
                <Typography sx={{ fontVariantNumeric: 'tabular-nums' }}>
                  {Number(pkg.revenue || 0).toFixed(0)}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </PartnerPageShell>
  );
};

export default PartnerAnalytics;
